import { UserGender } from '@/type';
import React, { useCallback, useContext, useState } from 'react';
import { SS_KEY_REG_DATA, SS_KEY_STEP_SIGNUP } from '@/utils/constants';
import { GOOGLE_ADS_METADATA_KEY } from '@/utils/google-ads-params';

interface SignUpType {
  anotherProfession?: string;
  anotherWorkplace?: string;
  dateOfBirth?: string;
  educationLevelId?: string;
  efnNumber?: string;
  email?: string;
  examYear?: number;
  firstName?: string;
  gender?: number;
  googleAdsMetadata?: Record<string, string>;
  lastName?: string;
  medicineStudyBegin?: string;
  password?: string;
  passwordConfirmation?: string;
  postcode?: string;
  professionId?: string;
  professionType?: number;
  registrationNumber?: string;
  semesterId?: string;
  specialityId?: string;
  studyBeginId?: string;
  studyYearId?: string;
  subscribeToNews: boolean;
  universityId?: string;
  username?: string;
  workplaceId?: string;
}

interface SignUpContextType extends SignUpType {
  update: (data: Partial<SignUpType>) => void;
  clean: () => void;
}
const EMPTY_STATE: SignUpType = {
  gender: UserGender.Male,
  subscribeToNews: true,
};

const readJson = <T,>(key: string): T | undefined => {
  const raw = sessionStorage.getItem(key);
  if (!raw) return undefined;

  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
};

// Runs at provider mount, not module load, so it reads the same sessionStorage snapshot as the
// step counter in `SS_KEY_STEP_SIGNUP`.
const buildInitialState = (): SignUpType => {
  const regData = readJson<Partial<SignUpType>>(SS_KEY_REG_DATA) || {};
  const googleAdsMetadata = readJson<Record<string, string>>(GOOGLE_ADS_METADATA_KEY);

  return {
    ...EMPTY_STATE,
    ...regData,
    ...(googleAdsMetadata && { googleAdsMetadata }),
  };
};

export const SignUpContext = React.createContext<SignUpContextType | undefined>(undefined);

export const SignUpProvider: React.FC = ({ children }) => {
  const [state, setState] = useState<SignUpType>(buildInitialState);

  const update = useCallback(
    (data: Partial<SignUpType>) => {
      sessionStorage.setItem(
        SS_KEY_REG_DATA,
        JSON.stringify({
          ...state,
          ...data,
        })
      );
      setState({
        ...state,
        ...data,
      });
    },
    [state]
  );

  const clean = useCallback(() => {
    sessionStorage.removeItem(SS_KEY_REG_DATA);
    sessionStorage.removeItem(SS_KEY_STEP_SIGNUP);
    sessionStorage.removeItem(GOOGLE_ADS_METADATA_KEY);
    setState(EMPTY_STATE);
  }, []);

  const globalState: SignUpContextType = React.useMemo(
    () => ({ ...state, clean, update }),
    [clean, state, update]
  );

  return <SignUpContext.Provider value={globalState}>{children}</SignUpContext.Provider>;
};

export const useSignUp = () => {
  const state = useContext(SignUpContext);

  if (!state) throw new Error('SignUpContext not found');
  return state;
};
